import { Badge, Button, ConfirmDialog, Input, Modal, Select, Surface, cn } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dueLeadDays, type DeliveryScheduleRow, type Weekday } from '@warehouse/shared';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../auth/auth-store';
import { PageHeader } from '../../../components/PageHeader';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import {
  ORDER_QUERY_KEYS,
  deliveryScheduleApi,
  portalApi,
  salesOrdersApi,
  type DeliveryScheduleFormValues,
} from '../api';
import { CustomDatesPanel } from '../components/CustomDatesPanel';

/**
 * "Leveransschema" — the exact port of the old WMS admin screen
 * (`src/pages/orders/delivery-schedule.tsx`, 887 lines).
 *
 * Kept: the accordion (one collapsible card per unit, NOT a flat table), the
 * Mon–Fri weekday grid with green "Leverans" / amber "Beställ senast HH:MM"
 * cells and the "senast N dagar innan" caption, the "N enheter saknar
 * standard-leveransschema" banner with clickable pills that pre-fill the create
 * modal, the self-pruning category dropdown, and the footer counts.
 *
 * Added: the holiday-move ("Hanterade datum") panel, which the old system
 * rendered read-only because it had no write path at all (recon §6.1).
 */

const WEEKDAYS: Weekday[] = [1, 2, 3, 4, 5];

interface ScheduleFormState {
  restaurantId: string;
  categoryId: string;
  deliveryDay1: string;
  dueDay1: string;
  dueTime1: string;
  deliveryDay2: string;
  dueDay2: string;
  dueTime2: string;
}

const EMPTY_FORM: ScheduleFormState = {
  restaurantId: '',
  categoryId: '',
  deliveryDay1: '',
  dueDay1: '',
  dueTime1: '',
  deliveryDay2: '',
  dueDay2: '',
  dueTime2: '',
};

const DEFAULT_CATEGORY_VALUE = '__default__';

export function DeliverySchedulePage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const queryClient = useQueryClient();
  const canManage = usePermission('orders.outbound.deliverySchedule');

  const [restaurantFilter, setRestaurantFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<ScheduleFormState | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DeliveryScheduleRow | null>(null);

  const listState = { page: 1, pageSize: 500 };
  const schedulesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.schedules(listState),
    queryFn: () => deliveryScheduleApi.list(listState),
  });
  const coverageQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.scheduleCoverage(),
    queryFn: () => deliveryScheduleApi.coverage(),
  });
  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });
  const categoriesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.allCategories(),
    queryFn: () => salesOrdersApi.categories(),
  });

  useLiveQuery(['delivery-schedules'], ['delivery-schedules']);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['delivery-schedules'] });
  };

  const saveMutation = useMutation({
    mutationFn: (values: DeliveryScheduleFormValues) =>
      editingId
        ? deliveryScheduleApi.update(editingId, {
            deliveryDay1: values.deliveryDay1,
            dueDay1: values.dueDay1,
            dueTime1: values.dueTime1,
            deliveryDay2: values.deliveryDay2,
            dueDay2: values.dueDay2,
            dueTime2: values.dueTime2,
          })
        : deliveryScheduleApi.create(values),
    onSuccess: () => {
      toastSuccess(editingId ? t('salesOrders:schedule.form.updated') : t('salesOrders:schedule.form.created'));
      setForm(null);
      setEditingId(null);
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deliveryScheduleApi.remove(id),
    onSuccess: () => {
      toastSuccess(t('salesOrders:schedule.delete.done'));
      setDeleting(null);
      invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const rows = schedulesQuery.data?.items ?? [];
  const coverage = coverageQuery.data?.items ?? [];
  const missingDefault = coverage.filter((c) => !c.hasDefault);

  const filtered = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!restaurantFilter || row.restaurantId === restaurantFilter) &&
          (!categoryFilter || row.categoryId === categoryFilter),
      ),
    [rows, restaurantFilter, categoryFilter],
  );

  const groups = useMemo(() => {
    const byRestaurant = new Map<string, { name: string; code: string; schedules: DeliveryScheduleRow[] }>();
    for (const row of filtered) {
      const entry = byRestaurant.get(row.restaurantId) ?? {
        name: row.restaurantName,
        code: row.restaurantCode,
        schedules: [],
      };
      entry.schedules.push(row);
      byRestaurant.set(row.restaurantId, entry);
    }
    // Restaurants with no schedule at all still deserve a row — that is how an
    // admin discovers them.
    for (const unit of coverage) {
      if (restaurantFilter && unit.restaurantId !== restaurantFilter) continue;
      if (categoryFilter) continue;
      if (!byRestaurant.has(unit.restaurantId)) {
        byRestaurant.set(unit.restaurantId, {
          name: unit.restaurantName,
          code: unit.restaurantCode,
          schedules: [],
        });
      }
    }
    return [...byRestaurant.entries()]
      .map(([restaurantId, value]) => ({ restaurantId, ...value }))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
  }, [filtered, coverage, restaurantFilter, categoryFilter]);

  // An active filter auto-expands what it matched, like the old screen did.
  const filtersActive = Boolean(restaurantFilter || categoryFilter);

  const usedCategoryIds = (restaurantId: string): Set<string> =>
    new Set(
      rows
        .filter((r) => r.restaurantId === restaurantId && r.categoryId)
        .map((r) => r.categoryId as string),
    );
  const hasDefault = (restaurantId: string): boolean =>
    rows.some((r) => r.restaurantId === restaurantId && r.categoryId === null);

  function openCreate(restaurantId = ''): void {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, restaurantId });
  }

  function openEdit(row: DeliveryScheduleRow): void {
    setEditingId(row.id);
    setForm({
      restaurantId: row.restaurantId,
      categoryId: row.categoryId ?? DEFAULT_CATEGORY_VALUE,
      deliveryDay1: String(row.deliveryDay1),
      dueDay1: String(row.dueDay1),
      dueTime1: row.dueTime1 ?? '',
      deliveryDay2: row.deliveryDay2 === null ? '' : String(row.deliveryDay2),
      dueDay2: row.dueDay2 === null ? '' : String(row.dueDay2),
      dueTime2: row.dueTime2 ?? '',
    });
  }

  return (
    <>
      <PageHeader
        title={t('salesOrders:schedule.title')}
        subtitle={t('salesOrders:schedule.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus className="size-4" aria-hidden />}
              onClick={() => {
                openCreate();
              }}
            >
              {t('salesOrders:schedule.actions.new')}
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select
          aria-label={t('salesOrders:schedule.filters.restaurant')}
          value={restaurantFilter}
          onChange={(event) => {
            setRestaurantFilter(event.target.value);
          }}
          options={[
            { value: '', label: t('salesOrders:schedule.filters.allRestaurants') },
            ...(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name })),
          ]}
        />
        <Select
          aria-label={t('salesOrders:schedule.filters.category')}
          value={categoryFilter}
          onChange={(event) => {
            setCategoryFilter(event.target.value);
          }}
          options={[
            { value: '', label: t('salesOrders:schedule.filters.allCategories') },
            ...(categoriesQuery.data?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        {filtersActive ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setRestaurantFilter('');
              setCategoryFilter('');
            }}
          >
            {t('common:actions.clearFilters')}
          </Button>
        ) : null}

        <span className="ml-auto flex items-center gap-3 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-green-600" aria-hidden />
            {t('salesOrders:schedule.legend.delivery')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-amber-500" aria-hidden />
            {t('salesOrders:schedule.legend.due')}
          </span>
        </span>
      </div>

      {missingDefault.length > 0 ? (
        <div
          className="mb-4 rounded-xl border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="missing-default-banner"
        >
          <p className="mb-2">{t('salesOrders:schedule.missingBanner', { count: missingDefault.length })}</p>
          <div className="flex flex-wrap gap-1.5">
            {missingDefault.map((unit) => (
              <button
                key={unit.restaurantId}
                type="button"
                disabled={!canManage}
                onClick={() => {
                  openCreate(unit.restaurantId);
                }}
                className="rounded-full border border-amber-400/60 bg-white px-2.5 py-1 text-xs transition-colors hover:bg-amber-100 disabled:opacity-60"
              >
                {unit.restaurantName}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2" data-testid="schedule-accordion">
        {groups.map((group) => {
          const open = filtersActive || expanded.has(group.restaurantId);
          const defaultRow = group.schedules.find((s) => s.categoryId === null);
          return (
            <Surface key={group.restaurantId} className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 p-4">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  onClick={() => {
                    setExpanded((current) => {
                      const next = new Set(current);
                      if (next.has(group.restaurantId)) next.delete(group.restaurantId);
                      else next.add(group.restaurantId);
                      return next;
                    });
                  }}
                >
                  {open ? (
                    <ChevronDown className="size-4 shrink-0 text-ink-muted" aria-hidden />
                  ) : (
                    <ChevronRight className="size-4 shrink-0 text-ink-muted" aria-hidden />
                  )}
                  <span className="min-w-0">
                    <span className="font-medium">{group.name}</span>
                    <span className="ml-2 text-xs text-ink-muted">{summarize(defaultRow, t)}</span>
                  </span>
                </button>

                <div className="flex items-center gap-1.5">
                  {defaultRow ? (
                    <Badge tone="success">{t('salesOrders:schedule.badges.default')}</Badge>
                  ) : (
                    <Badge tone="warning">{t('salesOrders:schedule.badges.missingDefault')}</Badge>
                  )}
                  {group.schedules.filter((s) => s.categoryId).length > 0 ? (
                    <Badge tone="neutral">
                      {t('salesOrders:schedule.badges.categories', {
                        count: group.schedules.filter((s) => s.categoryId).length,
                      })}
                    </Badge>
                  ) : null}
                  {canManage ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('salesOrders:schedule.actions.addForRestaurant')}
                      onClick={() => {
                        openCreate(group.restaurantId);
                      }}
                    >
                      <Plus className="size-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>
              </div>

              {open ? (
                <div className="overflow-x-auto border-t border-surface-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-ink-muted">
                        <th className="px-4 py-2">{t('salesOrders:schedule.columns.category')}</th>
                        {WEEKDAYS.map((day) => (
                          <th key={day} className="px-2 py-2 text-center">
                            {t(`salesOrders:weekday.${day}`)}
                          </th>
                        ))}
                        <th className="px-4 py-2 text-right">{t('salesOrders:schedule.columns.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.schedules.length === 0 ? (
                        <tr>
                          <td colSpan={WEEKDAYS.length + 2} className="px-4 py-4 text-ink-muted">
                            {t('salesOrders:schedule.summary.none')}
                          </td>
                        </tr>
                      ) : (
                        group.schedules
                          .slice()
                          .sort((a, b) =>
                            a.categoryId === null
                              ? -1
                              : b.categoryId === null
                                ? 1
                                : (a.categoryName ?? '').localeCompare(b.categoryName ?? '', 'sv'),
                          )
                          .map((row) => (
                            <tr key={row.id} className="border-b border-surface-border/60 last:border-0">
                              <td className="px-4 py-2">
                                {row.categoryName ?? t('salesOrders:schedule.defaultCategory')}
                              </td>
                              {WEEKDAYS.map((day) => (
                                <td key={day} className="px-2 py-2 text-center align-top">
                                  <WeekdayCell row={row} day={day} />
                                </td>
                              ))}
                              <td className="px-4 py-2 text-right">
                                {canManage ? (
                                  <span className="inline-flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`${t('salesOrders:schedule.actions.edit')} ${group.name}`}
                                      onClick={() => {
                                        openEdit(row);
                                      }}
                                    >
                                      <Pencil className="size-4" aria-hidden />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      aria-label={`${t('salesOrders:schedule.actions.delete')} ${group.name}`}
                                      onClick={() => {
                                        setDeleting(row);
                                      }}
                                    >
                                      <Trash2 className="size-4" aria-hidden />
                                    </Button>
                                  </span>
                                ) : null}
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </Surface>
          );
        })}
      </div>

      <p className="mt-4 text-xs text-ink-muted" data-testid="schedule-footer">
        {t('salesOrders:schedule.footer', {
          shown: groups.length,
          total: coverage.length,
          schedules: rows.length,
          missing: missingDefault.length,
        })}
      </p>

      <div className="mt-8">
        <CustomDatesPanel canManage={canManage} restaurantId={restaurantFilter || undefined} />
      </div>

      {form ? (
        <ScheduleFormModal
          form={form}
          editing={editingId !== null}
          restaurants={(restaurantsQuery.data?.items ?? []).map((r) => ({ id: r.id, name: r.name }))}
          categories={categoriesQuery.data?.items ?? []}
          usedCategoryIds={usedCategoryIds(form.restaurantId)}
          restaurantHasDefault={hasDefault(form.restaurantId)}
          saving={saveMutation.isPending}
          onChange={setForm}
          onClose={() => {
            setForm(null);
            setEditingId(null);
          }}
          onSubmit={(values) => {
            saveMutation.mutate(values);
          }}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          variant="danger"
          title={t('salesOrders:schedule.delete.title')}
          message={t('salesOrders:schedule.delete.message', { restaurant: deleting.restaurantName })}
          confirmLabel={t('salesOrders:schedule.delete.confirm')}
          cancelLabel={t('common:actions.cancel')}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deleting.id);
          }}
          onClose={() => {
            setDeleting(null);
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

/** A weekday cell can show BOTH states at once — delivery and order-by. */
function WeekdayCell({ row, day }: { row: DeliveryScheduleRow; day: Weekday }): ReactNode {
  const { t } = useTranslation('salesOrders');
  const isDelivery = row.deliveryDay1 === day || row.deliveryDay2 === day;
  const dueTime = row.dueDay1 === day ? row.dueTime1 : row.dueDay2 === day ? row.dueTime2 : null;
  const isDue = row.dueDay1 === day || row.dueDay2 === day;

  if (!isDelivery && !isDue) return <span className="text-ink-muted/50">–</span>;

  const cycles = [
    { deliveryDay: row.deliveryDay1 as Weekday, dueDay: row.dueDay1 as Weekday, dueTime: row.dueTime1 },
    ...(row.deliveryDay2 !== null && row.dueDay2 !== null
      ? [{ deliveryDay: row.deliveryDay2 as Weekday, dueDay: row.dueDay2 as Weekday, dueTime: row.dueTime2 }]
      : []),
  ];
  const lead = dueLeadDays({ restaurantId: row.restaurantId, categoryId: row.categoryId, cycles }, day);

  return (
    <div className={cn('flex flex-col items-center gap-0.5 rounded-md py-1', isDelivery && isDue && 'bg-gradient-to-b from-green-50 to-amber-50')}>
      {isDelivery ? (
        <span className="text-xs font-semibold text-green-800">{t('schedule.cell.delivery')}</span>
      ) : null}
      {isDue ? (
        <span className="text-xs font-semibold text-amber-800">
          {t('schedule.cell.due')}
          {dueTime ? ` ${dueTime}` : ''}
        </span>
      ) : null}
      {isDue && lead !== null ? (
        <span className="text-[10px] text-amber-700">{t('schedule.cell.leadDays', { count: lead })}</span>
      ) : null}
    </div>
  );
}

function summarize(row: DeliveryScheduleRow | undefined, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!row) return t('salesOrders:schedule.summary.none');
  const days = (values: (number | null)[]): string =>
    values
      .filter((v): v is number => v !== null)
      .map((v) => t(`salesOrders:weekdayShort.${v}`))
      .join(', ');
  const due = [
    row.dueDay1 !== null ? `${t(`salesOrders:weekdayShort.${row.dueDay1}`)}${row.dueTime1 ? ` ${row.dueTime1}` : ''}` : null,
    row.dueDay2 !== null ? `${t(`salesOrders:weekdayShort.${row.dueDay2}`)}${row.dueTime2 ? ` ${row.dueTime2}` : ''}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  return `${t('salesOrders:schedule.summary.delivery', { days: days([row.deliveryDay1, row.deliveryDay2]) })} · ${t('salesOrders:schedule.summary.due', { days: due })}`;
}

// ---------------------------------------------------------------------------

function ScheduleFormModal({
  form,
  editing,
  restaurants,
  categories,
  usedCategoryIds,
  restaurantHasDefault,
  saving,
  onChange,
  onClose,
  onSubmit,
}: {
  form: ScheduleFormState;
  editing: boolean;
  restaurants: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  usedCategoryIds: Set<string>;
  restaurantHasDefault: boolean;
  saving: boolean;
  onChange: (form: ScheduleFormState) => void;
  onClose: () => void;
  onSubmit: (values: DeliveryScheduleFormValues) => void;
}): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [error, setError] = useState<string | null>(null);

  const dayOptions = [
    { value: '', label: t('salesOrders:schedule.form.dayPlaceholder') },
    ...WEEKDAYS.map((day) => ({ value: String(day), label: t(`salesOrders:weekday.${day}`) })),
  ];

  // Self-pruning, exactly like the old modal: only offer "Standard" when the
  // unit has no default yet, and hide categories that already have a row.
  const categoryOptions = [
    ...(restaurantHasDefault && !editing
      ? []
      : [{ value: DEFAULT_CATEGORY_VALUE, label: t('salesOrders:schedule.defaultCategory') }]),
    ...categories
      .filter((c) => editing || !usedCategoryIds.has(c.id))
      .map((c) => ({ value: c.id, label: c.name })),
  ];

  function submit(): void {
    if (!editing && !form.restaurantId) {
      setError(t('salesOrders:schedule.form.errors.restaurantRequired'));
      return;
    }
    if (!editing && !form.categoryId) {
      setError(t('salesOrders:schedule.form.errors.categoryRequired'));
      return;
    }
    if (!form.deliveryDay1 || !form.dueDay1 || !form.deliveryDay2 || !form.dueDay2) {
      setError(t('salesOrders:schedule.form.errors.daysRequired'));
      return;
    }
    setError(null);
    onSubmit({
      restaurantId: form.restaurantId,
      categoryId: form.categoryId === DEFAULT_CATEGORY_VALUE ? null : form.categoryId,
      deliveryDay1: Number(form.deliveryDay1),
      dueDay1: Number(form.dueDay1),
      dueTime1: form.dueTime1 || null,
      deliveryDay2: Number(form.deliveryDay2),
      dueDay2: Number(form.dueDay2),
      dueTime2: form.dueTime2 || null,
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? t('salesOrders:schedule.form.editTitle') : t('salesOrders:schedule.form.createTitle')}
      width={640}
    >
      <div className="flex flex-col gap-4">
        <Select
          label={t('salesOrders:schedule.form.restaurant')}
          placeholder={t('salesOrders:schedule.form.restaurantPlaceholder')}
          disabled={editing}
          value={form.restaurantId}
          onChange={(event) => {
            onChange({ ...form, restaurantId: event.target.value, categoryId: '' });
          }}
          options={restaurants.map((r) => ({ value: r.id, label: r.name }))}
        />

        <Select
          label={t('salesOrders:schedule.form.category')}
          placeholder={t('salesOrders:schedule.form.categoryPlaceholder')}
          disabled={editing}
          value={form.categoryId}
          onChange={(event) => {
            onChange({ ...form, categoryId: event.target.value });
          }}
          options={categoryOptions}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <CycleFields
            index={1}
            deliveryDay={form.deliveryDay1}
            dueDay={form.dueDay1}
            dueTime={form.dueTime1}
            dayOptions={dayOptions}
            onChange={(patch) => {
              onChange({ ...form, ...patch });
            }}
          />
          <CycleFields
            index={2}
            deliveryDay={form.deliveryDay2}
            dueDay={form.dueDay2}
            dueTime={form.dueTime2}
            dayOptions={dayOptions}
            onChange={(patch) => {
              onChange({ ...form, ...patch });
            }}
          />
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={saving} onClick={submit}>
            {t('salesOrders:schedule.form.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CycleFields({
  index,
  deliveryDay,
  dueDay,
  dueTime,
  dayOptions,
  onChange,
}: {
  index: 1 | 2;
  deliveryDay: string;
  dueDay: string;
  dueTime: string;
  dayOptions: { value: string; label: string }[];
  onChange: (patch: Partial<ScheduleFormState>) => void;
}): ReactNode {
  const { t } = useTranslation('salesOrders');
  const key = <K extends string>(name: K): string => `${name}${index}`;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-surface-border p-3">
      <Select
        label={t(`schedule.form.deliveryDay${index}`)}
        value={deliveryDay}
        onChange={(event) => {
          onChange({ [key('deliveryDay')]: event.target.value } as Partial<ScheduleFormState>);
        }}
        options={dayOptions}
      />
      <Select
        label={t(`schedule.form.dueDay${index}`)}
        value={dueDay}
        onChange={(event) => {
          onChange({ [key('dueDay')]: event.target.value } as Partial<ScheduleFormState>);
        }}
        options={dayOptions}
      />
      <label className="flex flex-col gap-1 text-sm">
        <span className="flex items-center justify-between gap-2">
          <span className="font-medium">{t('schedule.form.cutoffTime')}</span>
          {dueTime ? (
            <button
              type="button"
              className="text-xs text-brand-600 hover:underline"
              onClick={() => {
                onChange({ [key('dueTime')]: '' } as Partial<ScheduleFormState>);
              }}
            >
              {t('schedule.form.clearTime')}
            </button>
          ) : null}
        </span>
        <Input
          type="time"
          value={dueTime}
          onChange={(event) => {
            onChange({ [key('dueTime')]: event.target.value } as Partial<ScheduleFormState>);
          }}
        />
      </label>
    </div>
  );
}
